import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import CheckboxComponent from '#app/Component/CheckboxComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { Form, FormField, initFormHandler } from '#app/Handler/FormHandler';
import { Product } from '#app/View/ClientService/OrderClientLitige';
import Modal from '#app/View/ClientService/OrderClientLitige/components/Modal';

type Props = {
  reload: () => void;
  onClose: () => void;
  products: Product[];
  carrierPrice: number;
  canRefundCarrier: boolean;
  canRefundPayment: boolean;
};

const RefundModal: React.FC<Props> = ({
  onClose,
  reload,
  products,
  carrierPrice,
  canRefundCarrier,
  canRefundPayment,
}) => {
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid as string;
  const [loading, setLoading] = useState<boolean>(false);
  const classes = useStyles();

  const [form, setForm] = useState<Form>({
    ...(canRefundPayment && {
      isPayment: {
        name: 'isPayment',
        label: 'Paiement',
        textHelper: 'Remboursement des frais de paiement.',
        type: 'boolean',
        defaultValue: false,
        value: false,
      },
    }),
    ...(canRefundCarrier && {
      isCarrier: {
        name: 'isCarrier',
        label: 'Transport',
        textHelper: 'Remboursement du transport.',
        type: 'boolean',
        defaultValue: false,
        value: false,
      },
      carrierPrice: {
        name: 'carrierPrice',
        label: 'Prix TTC du transport',
        textHelper: 'Remboursement du prix TTC du transport.',
        type: 'float',
        defaultValue: carrierPrice,
        options: { max: carrierPrice },
      },
    }),
    ...products.reduce((acc: Record<string, FormField>, product) => {
      acc[`products.${product.id}`] = {
        name: `products.${product.id}`,
        label: 'Quantité',
        textHelper: `Saisissez la quantité de produit en litige (maximum : ${product.available})`,
        type: 'integer',
        options: { max: product.available },
      };
      return acc;
    }, {}),
  });
  const handler = initFormHandler(form, setForm);

  useEffect(() => {
    handler.start();
  }, []);

  const handleSubmit = (): void => {
    if (handler.checkError() > 0) {
      return;
    }
    const data = handler.getData();
    const products = Object.entries(data)
      .filter(([key]) => key.startsWith('products.'))
      .map(([key, value]) => ({
        quantity: value ?? 0,
        byProduct: parseInt(key.split('.')[1], 10),
      }));

    if (!products.some((product) => product.quantity > 0)) {
      dispatch(SnackbarOpen({ text: 'Il faut au moins un produit', variant: 'error' }));

      return;
    }

    setLoading(true);
    ApiHandler.post(
      {
        route: 'api_client_service_order_client_litige_refund',
        params: { uuid },
        data: {
          isPayment: data.isPayment,
          isCarrier: data.isCarrier,
          carrierPrice: data.carrierPrice,
          products,
        },
      },
      (response) => {
        if (response.status === 200) {
          dispatch(
            SnackbarOpen({
              text: 'Demande de remboursement créée avec succès.',
              variant: 'success',
            }),
          );
          reload();
        } else {
          dispatch(
            SnackbarOpen({
              text: response?.error?.message || "Une erreur inattendue s'est produite.",
              variant: 'error',
            }),
          );
        }
        setLoading(false);
        onClose();
      },
    );
  };

  return (
    <Modal title="Remboursement" onClose={onClose} onSubmit={handleSubmit} loading={loading}>
      <Grid container>
        {canRefundCarrier && (
          <>
            <Grid size={6}>
              <CheckboxComponent id="isCarrier" handler={handler} />
            </Grid>
            <Grid size={6}>
              <TextFieldComponent id="carrierPrice" handler={handler} disabled={!handler.getData().isCarrier} />
            </Grid>
          </>
        )}

        {canRefundPayment && <CheckboxComponent id="isPayment" handler={handler} />}

        {(canRefundCarrier || canRefundPayment) && !!products.length && (
          <Grid size={12}>
            <hr />
          </Grid>
        )}
        {products.map((product) => (
          <React.Fragment key={product.id}>
            <Grid size={6} className={classes.productLabel}>
              {product.name}
            </Grid>
            <Grid size={6}>
              <TextFieldComponent id={`products.${product.id}`} handler={handler} />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Modal>
  );
};

const useStyles = makeStyles({
  productLabel: {
    paddingTop: '10px',
  },
});

export default RefundModal;
